import { kAnalyticsFeatureMatterDriveSync } from '@/analytics/constants'
import { AnalyticsEvent, AnalyticsEventProperties } from '@/analytics/schema/events.schema'
import type { TrackEventFn } from '@/analytics/hooks/useAnalytics'
import type { UserAccountData } from '@/firebase/auth/user-account-data'

/**
 * Organization id for Continuous Matter Drive Sync analytics events.
 *
 * Uses `orgData.id` only — the same source as `OrganizationInviteSent` and Matter collaboration
 * events (`getCollaborationOrganizationId`) — so org-level drive-sync dashboards join on the same
 * key as other org-scoped analytics. See analytics/README.md ("Matter Drive Sync").
 */
export function getDriveSyncOrganizationId(userAccountData: UserAccountData | null | undefined): string {
  return userAccountData?.orgData?.id ?? ''
}

/** Continuous Matter Drive Sync events (DAT-12 + discovery), narrowed from the full AnalyticsEvent union. */
type MatterDriveSyncEvent =
  | AnalyticsEvent.MatterDriveSyncSetupEntryClicked
  | AnalyticsEvent.MatterDriveSyncDiscoveryClicked
  | AnalyticsEvent.MatterDriveSyncAccountConnectModalOpened
  | AnalyticsEvent.MatterDriveSyncConnectRedirectStarted
  | AnalyticsEvent.MatterDriveSyncConnectRedirectFailed
  | AnalyticsEvent.MatterDriveSyncAccountConnected
  | AnalyticsEvent.MatterDriveSyncAccountDisconnected
  | AnalyticsEvent.MatterDriveSyncAccountDisconnectFailed
  | AnalyticsEvent.MatterDriveSyncFolderPickerOpened
  | AnalyticsEvent.MatterDriveSyncFolderBrowseFailed
  | AnalyticsEvent.MatterDriveSyncFolderPickerDismissed
  | AnalyticsEvent.MatterDriveSyncFolderMapped
  | AnalyticsEvent.MatterDriveSyncFolderMapFailed
  | AnalyticsEvent.MatterDriveSyncFolderDisconnected
  | AnalyticsEvent.MatterDriveSyncFolderDisconnectFailed

/**
 * Track a Continuous Matter Drive Sync event (DAT-12). Always injects
 * `feature: 'matter_drive_sync'` and suppresses route-derived feature inheritance, so call
 * sites don't need to repeat both — mirrors `trackMedChronEvent` / `trackTimelineEvent`.
 *
 * Unlike those helpers, `track` has **no default fallback to `getTrackEventGlobal()`**: every
 * Drive Sync call site lives inside a React component or hook, so callers must always pass the
 * `trackEvent` returned by `useAnalytics()` explicitly (see analytics/README.md,
 * "trackEvent vs trackEventGlobal" — the global bridge is reserved for non-React contexts).
 */
export function trackMatterDriveSyncEvent<T extends MatterDriveSyncEvent>(
  event: T,
  props: Omit<AnalyticsEventProperties[T], 'feature'>,
  track: TrackEventFn
): void {
  track(event, { ...props, feature: kAnalyticsFeatureMatterDriveSync } as AnalyticsEventProperties[T], {
    suppressFeatureInheritance: true,
  })
}
